import React, { useState, useEffect, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Box, Button, ButtonGroup, Card, CardContent, Checkbox, Chip, FormControl, Grid, IconButton,
  InputAdornment, InputLabel, MenuItem, Select, Table, TableBody, TableCell, TableHead, TableRow,
  TextField, Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { DateTime } from 'luxon';
import RestAPIService from '../../Utility/Services/RestAPIService';
import { Serviceurls } from '../../Utility/API/Serviceurls';
import ToastMsg from '../../Components/ToastMsg/ToastMsg';
import Loader from '../../Components/Loader';
import { ImagePaths } from '../../Utility/ImagePaths';
import { Colors } from '../../Styles/Colors';

const GET_TYPE_VITALS = 'Vitals';
const GET_TYPE_CARE = 'Care';
const HOURS_OPTIONS = [2, 4, 8, 12, 24];
const PRIORITY_COLOR = { High: 'error', Medium: 'warning', Low: 'info' };
const STATUS_COLOR = { Pending: 'warning', Completed: 'success', "Medication Ready": 'success', 'In Progress': 'info' };

export default function NurseTask() {
  const history = useHistory();
  const [patientList, setPatientList] = useState([]);
  const [taskList, setTaskList] = useState([]);
  const [specialTaskList, setSpecialTaskList] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [taskType, setTaskType] = useState('Vitals');
  const [hoursFilter, setHoursFilter] = useState(2);
  const [blockFilter, setBlockFilter] = useState('');
  const [floorFilter, setFloorFilter] = useState('');
  const [wardFilter, setWardFilter] = useState('');
  const [dateFilter, setDateFilter] = useState(DateTime.now().toFormat('yyyy-MM-dd'));
  const [searchKey, setSearchKey] = useState('');
  const [isLoader, setIsLoader] = useState(false);
  const [isErrorMsg, setIsErrorMsg] = useState(false);
  const [isErrorMsgText, setIsErrorMsgText] = useState('');
  const [successMsg, setSuccessMsg] = useState(false);
  const [successMsgText, setSuccessMsgText] = useState('');
  const [popupOpen, setPopupOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [selectedCareTaskIds, setSelectedCareTaskIds] = useState([]);
  const [treatmentPopupOpen, setTreatmentPopupOpen] = useState(false);
  const [treatmentTask, setTreatmentTask] = useState(null);

  const getTypeForPatientList = taskType === 'Special Tasks' ? GET_TYPE_CARE : GET_TYPE_VITALS;

  const fetchPatientList = useCallback(() => {
    setIsLoader(true);
    RestAPIService.getAll(`${Serviceurls.IP_TASK_PATIENT_LIST}?get_type=${getTypeForPatientList}`)
      .then((res) => {
        if (res.data?.status === 'success') {
          setPatientList(res.data?.data || []);
        } else setPatientList([]);
        setIsLoader(false);
      })
      .catch(() => {
        setPatientList([]);
        setIsLoader(false);
      });
  }, [getTypeForPatientList]);

  const fetchTaskList = useCallback(() => {
    const params = new URLSearchParams({ get_type: GET_TYPE_VITALS, hours: hoursFilter, date: dateFilter });
    if (selectedPatient?.ip_admission_id) params.append('ip_admission_id', selectedPatient.ip_admission_id);
    setIsLoader(true);
    RestAPIService.getAll(`${Serviceurls.IP_PATIENT_NURSE_TASKS}?${params}`)
      .then((res) => {
        if (res.data?.status === 'success') {
          setTaskList(res.data?.data || []);
        } else setTaskList([]);
        setIsLoader(false);
      })
      .catch(() => {
        setTaskList([]);
        setIsLoader(false);
      });
  }, [hoursFilter, selectedPatient?.ip_admission_id]);

  const fetchSpecialTaskList = useCallback(() => {
    const params = new URLSearchParams({ get_type: GET_TYPE_CARE, hours: hoursFilter });
    if (selectedPatient?.ip_admission_id) params.append('ip_admission_id', selectedPatient.ip_admission_id);
    setIsLoader(true);
    RestAPIService.getAll(`${Serviceurls.IP_PATIENT_NURSE_TASKS}?${params}`)
      .then((res) => {
        if (res.data?.status === 'success') {
          setSpecialTaskList(res.data?.data || []);
        } else setSpecialTaskList([]);
        setIsLoader(false);
      })
      .catch(() => {
        setSpecialTaskList([]);
        setIsLoader(false);
      });
  }, [hoursFilter, selectedPatient?.ip_admission_id]);

  useEffect(() => { fetchPatientList(); }, [fetchPatientList]);
  useEffect(() => {
    if (taskType === 'Vitals') fetchTaskList();
    else fetchSpecialTaskList();
  }, [taskType, fetchTaskList, fetchSpecialTaskList]);
  useEffect(() => { setSelectedCareTaskIds([]); }, [taskType, specialTaskList]);

  const blocks = [...new Set(patientList.map((p) => p.block_name).filter(Boolean))];
  const floors = [...new Set(patientList.map((p) => p.floor_number).filter(Boolean))];
  const wards = [...new Set(patientList.map((p) => p.ward_name).filter(Boolean))];

  const filteredPatients = patientList.filter((p) => {
    if (blockFilter && p.block_name !== blockFilter) return false;
    if (floorFilter && String(p.floor_number) !== String(floorFilter)) return false;
    if (wardFilter && p.ward_name !== wardFilter) return false;
    if (searchKey && searchKey.length >= 2) {
      const key = searchKey.toLowerCase();
      if (!(p.patient_name || '').toLowerCase().includes(key) &&
        !(p.patient_account_number || '').toLowerCase().includes(key) &&
        !(p.mobile_number || '').includes(searchKey)) return false;
    }
    return true;
  });

  const openTaskPopup = (task, patient) => {
    setSelectedTask(task);
    setPopupOpen(true);
  };

  const closePopup = () => {
    setPopupOpen(false);
    setSelectedTask(null);
  };

  const isCareTask = (task) => (task.task_type || task.task_ype || '').toLowerCase().includes('care');
  const isTreatmentTask = (task) => (task.task_type || task.task_ype || '').toLowerCase().includes('treatment');

  const handleCareCompleteSingle = (task, e) => {
    e?.stopPropagation?.();
    if (!task?.id || task.status === 'Completed') return;
    setIsLoader(true);
    const payload = { id: [task.id], get_type: 'Care' };
    RestAPIService.create(payload, Serviceurls.IP_PATIENT_NURSE_TASKS)
      .then((res) => {
        setIsLoader(false);
        if (res.data?.status === 'success') {
          showSuccess(res.data?.message || 'Task completed.');
          fetchSpecialTaskList();
        } else showError(res.data?.message || 'Failed to complete.');
      })
      .catch((err) => {
        setIsLoader(false);
        showError(err?.response?.data?.message || err?.message || 'Failed to complete.');
      });
  };

  const handleCareCompleteMultiple = () => {
    const ids = selectedCareTaskIds.filter((id) => {
      const t = specialTaskList.find((x) => x.id === id);
      return t && t.status !== 'Completed';
    });
    if (ids.length === 0) {
      showError('Select at least one pending care task.');
      return;
    }
    setIsLoader(true);
    const payload = { id: ids, get_type: 'Care' };
    RestAPIService.create(payload, Serviceurls.IP_PATIENT_NURSE_TASKS)
      .then((res) => {
        setIsLoader(false);
        setSelectedCareTaskIds([]);
        if (res.data?.status === 'success') {
          showSuccess(res.data?.message || 'Selected tasks completed.');
          fetchSpecialTaskList();
        } else showError(res.data?.message || 'Failed to complete.');
      })
      .catch((err) => {
        setIsLoader(false);
        showError(err?.response?.data?.message || err?.message || 'Failed to complete.');
      });
  };

  const toggleCareTaskSelection = (id, checked) => {
    setSelectedCareTaskIds((prev) =>
      checked ? [...prev, id] : prev.filter((x) => x !== id)
    );
  };

  const openTreatmentPopup = (task) => {
    setTreatmentTask(task);
    setTreatmentPopupOpen(true);
  };

  const closeTreatmentPopup = () => {
    setTreatmentPopupOpen(false);
    setTreatmentTask(null);
  };

  const showError = (msg) => { setIsErrorMsg(true); setIsErrorMsgText(msg); };
  const showSuccess = (msg) => { setSuccessMsg(true); setSuccessMsgText(msg); };
  const msgClose = () => { setIsErrorMsg(false); setSuccessMsg(false); };

  const getTaskCardBorderColor = (task, index) => {
    if (task?.status === 'Pending' || task?.status === 'Overdue') return '#d32f2f';
    if (task?.status === 'Ontime' || task?.status === 'Completed') return '#2e7d32';
    return index % 2 === 0 ? '#00897b' : '#1976d2';
  };

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 120px)', overflow: 'hidden', bgcolor: 'background.default' }}>
      <Box sx={{ width: 280, borderRight: '1px solid', borderColor: 'divider', display: 'flex', flexDirection: 'column', bgcolor: 'grey.50' }}>
        <Box sx={{ p: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Search Patients Name, Number, UHID"
            value={searchKey}
            onChange={(e) => setSearchKey(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
            sx={{ mb: 1.5 }}
          />
          <ButtonGroup size="small" fullWidth disableElevation sx={{ '& .MuiButton-root': { textTransform: 'capitalize' } }}>
            <Button variant={taskType === 'Vitals' ? 'contained' : 'outlined'} onClick={() => setTaskType('Vitals')}>Vitals</Button>
            <Button variant={taskType === 'Special Tasks' ? 'contained' : 'outlined'} onClick={() => setTaskType('Special Tasks')}>Special Tasks</Button>
          </ButtonGroup>
        </Box>
        <Box sx={{ p: 1.5, overflow: 'auto', flex: 1 }}>
          {filteredPatients.map((p) => (
            <Card
              key={p.ip_admission_id}
              variant="outlined"
              sx={{
                mb: 1,
                cursor: 'pointer',
                border: '1px solid',
                borderColor: 'divider',
                borderLeft: selectedPatient?.ip_admission_id === p.ip_admission_id ? '4px solid' : '1px solid',
                borderLeftColor: selectedPatient?.ip_admission_id === p.ip_admission_id ? 'primary.main' : 'divider',
                borderRadius: 1,
                '&:hover': { bgcolor: 'action.hover' },
              }}
              onClick={() => setSelectedPatient(selectedPatient?.ip_admission_id === p.ip_admission_id ? null : p)}
            >
              <CardContent sx={{ py: 1.25, px: 1.5, '&:last-child': { pb: 1.25 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box component="img" src={p.photo_url || (p.patient_gender === 'm' || p.patient_gender === 'Male' ? ImagePaths.Male?.default : ImagePaths.Female?.default)} alt="" sx={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover' }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle2" noWrap>{p.patient_name} {p.patient_age}</Typography>
                    <Typography variant="caption" color="text.secondary" display="block">{p.patient_account_number} | {p.mobile_number}</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      </Box>

      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <Box sx={{ p: 2, display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Select Block</InputLabel>
            <Select value={blockFilter} label="Select Block" onChange={(e) => setBlockFilter(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              {blocks.map((b) => (<MenuItem key={b} value={b}>{b}</MenuItem>))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Select Floor</InputLabel>
            <Select value={floorFilter} label="Select Floor" onChange={(e) => setFloorFilter(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              {floors.map((f) => (<MenuItem key={f} value={f}>{f}</MenuItem>))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Select Ward</InputLabel>
            <Select value={wardFilter} label="Select Ward" onChange={(e) => setWardFilter(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              {wards.map((w) => (<MenuItem key={w} value={w}>{w}</MenuItem>))}
            </Select>
          </FormControl>
          <TextField type="date" size="small" value={dateFilter} onChange={(e) => setDateFilter(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: 160 }} />
        </Box>

        <Box sx={{ p: 2, flex: 1, overflow: 'auto', bgcolor: 'background.default' }}>
          {taskType === 'Special Tasks' ? (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 2, borderBottom: '1px solid', borderColor: 'divider', pb: 1.5, width: '100%' }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>Task Details</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <ButtonGroup
                    size="small"
                    disableElevation
                    sx={{
                      '& .MuiButton-root': { textTransform: 'capitalize', minWidth: 64, px: 1.5, borderColor: 'divider' },
                      '& .MuiButtonGroup-grouped:not(:last-of-type)': { borderColor: 'divider' },
                    }}
                  >
                    {HOURS_OPTIONS.map((h) => (
                      <Button
                        key={h}
                        variant={hoursFilter === h ? 'contained' : 'outlined'}
                        onClick={() => setHoursFilter(h)}
                        sx={{ fontWeight: hoursFilter === h ? 600 : 400 }}
                      >
                        {h} Hrs
                      </Button>
                    ))}
                  </ButtonGroup>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleCareCompleteMultiple}
                    disabled={selectedCareTaskIds.length === 0}
                    sx={{ textTransform: 'uppercase', bgcolor: Colors.Primary }}
                  >
                    Completed
                  </Button>
                </Box>
              </Box>
              <Box sx={{ overflow: 'auto' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.100' }}>
                      <TableCell padding="checkbox"></TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Task Type</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Doctor Name</TableCell>
                      <TableCell sx={{ fontWeight: 600, width: '15dvw' }}>Patient Details</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Task Name</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Instructions</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Priority</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Task Time</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600 }}>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {specialTaskList.map((row) => {
                      const patient = patientList.find((p) => p.ip_admission_id === row.ip_admission_id) || {};
                      const care = isCareTask(row);
                      const treatment = isTreatmentTask(row);
                      const pending = row.status !== 'Completed';
                      const checked = selectedCareTaskIds.includes(row.id);
                      return (
                        <TableRow key={row.id} hover>
                          <TableCell padding="checkbox">
                            {care ? (
                              <Checkbox
                                checked={checked}
                                disabled={!pending}
                                onChange={(e) => toggleCareTaskSelection(row.id, e.target.checked)}
                                onClick={(e) => e.stopPropagation()}
                              />
                            ) : (
                              <span />
                            )}
                          </TableCell>
                          <TableCell>{row.task_type || row.task_ype || '-'}</TableCell>
                          <TableCell>{row.doctor_name || '-'}</TableCell>
                          <TableCell>
                            {patient.patient_name ? (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box component="img" src={patient.photo_url || (patient.patient_gender === 'm' || patient.patient_gender === 'Male' ? ImagePaths.Male?.default : ImagePaths.Female?.default)} alt="" sx={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover' }} />
                                <Box>
                                  <Typography variant="body2">{patient.patient_name} {patient.patient_age}</Typography>
                                  <Typography variant="caption" color="text.secondary">{patient.patient_account_number} | {patient.mobile_number}</Typography>
                                </Box>
                              </Box>
                            ) : (
                              row.task_datetime || '-'
                            )}
                          </TableCell>
                          <TableCell>{(row.task_name && row?.duration) ? `${row.task_name} (${row.duration} ${row.duration_mode == 'Minutes' ? 'Mins' : ''})` : row.task_name || '-'}</TableCell>
                          <TableCell sx={{ maxWidth: 160 }}>{(row.instructions || '-').length > 25 ? (row.instructions || '').slice(0, 25) + '...' : (row.instructions || '-')}</TableCell>
                          <TableCell>
                            <Chip size="small" label={row.priority || '-'} color={PRIORITY_COLOR[row.priority] || 'default'} />
                          </TableCell>
                          <TableCell>{row.task_datetime || '-'}</TableCell>
                          <TableCell>
                            <Chip size="small" label={row.status || 'Pending'} color={STATUS_COLOR[row.status] || 'default'} />
                          </TableCell>
                          <TableCell align="center">
                            {care && pending && (
                              <IconButton size="small" color="success" onClick={(e) => handleCareCompleteSingle(row, e)} title="Complete">
                                <CheckCircleOutlineIcon fontSize="small" />
                              </IconButton>
                            )}
                            {treatment && (
                              <IconButton size="small" color="primary" onClick={() => openTreatmentPopup(row)} title="Treatment plan">
                                <AssignmentIcon fontSize="small" />
                              </IconButton>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>
              {specialTaskList.length === 0 && !isLoader && (
                <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>No special tasks</Typography>
              )}
            </>
          ) : (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 2, borderBottom: '1px solid', borderColor: 'divider', pb: 1.5, width: '100%' }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>Upcoming Tasks</Typography>
                <ButtonGroup
                  size="small"
                  disableElevation
                  sx={{
                    '& .MuiButton-root': { textTransform: 'capitalize', minWidth: 64, px: 1.5, borderColor: 'divider' },
                    '& .MuiButtonGroup-grouped:not(:last-of-type)': { borderColor: 'divider' },
                  }}
                >
                  {HOURS_OPTIONS.map((h) => (
                    <Button
                      key={h}
                      variant={hoursFilter === h ? 'contained' : 'outlined'}
                      onClick={() => setHoursFilter(h)}
                      sx={{ fontWeight: hoursFilter === h ? 600 : 400 }}
                    >
                      {h} Hrs
                    </Button>
                  ))}
                </ButtonGroup>
              </Box>
              <Grid container spacing={2}>
                {taskList.map((task, index) => {
                  const patient = patientList.find((p) => p.ip_admission_id === task.ip_admission_id) || {};
                  const borderColor = getTaskCardBorderColor(task, index);
                  return (
                    <Grid item xs={12} sm={6} md={4} key={task.id}>
                      <Card
                        variant="outlined"
                        sx={{
                          cursor: 'pointer',
                          border: '1px solid',
                          borderColor: 'divider',
                          borderLeft: '4px solid',
                          borderLeftColor: borderColor,
                          borderRadius: 1,
                          overflow: 'hidden',
                          '&:hover': { boxShadow: 1, borderColor: 'grey.400' },
                        }}
                        onClick={() => openTaskPopup(task, patient)}
                      >
                        <CardContent sx={{ py: 1.5, px: 1.5, '&:last-child': { pb: 1.5 } }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexDirection: { xs: 'column', sm: 'row' }, gap: 1.25, mb: 1 }}>
                            <Box sx={{ display: 'flex', width: '20dvw', alignItems: 'center', gap: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
                              <Box component="img" src={patient.photo_url || (patient.patient_gender === 'm' || patient.patient_gender === 'Male' ? ImagePaths.Male?.default : ImagePaths.Female?.default)} alt="" sx={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover' }} />
                              <Typography variant="subtitle2" fontWeight={500}>{patient.patient_name || '-'} {patient.patient_age}</Typography>
                            </Box>
                            <Box sx={{ flex: 1, ml: 5 }}>
                              <Typography variant="caption" color="text.secondary" display="block">Vital Time</Typography>
                              <Typography variant="body2" noWrap >{task.scheduled_time || '-'}</Typography>
                            </Box>  
                          </Box>
                          <Typography variant="caption" color="text.secondary" display="block">{patient.patient_account_number} | {patient.mobile_number}</Typography>
                          <Box sx={{ display: 'flex', mt: 1.25, gap: 1 }}>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography variant="caption" color="text.secondary" display="block">Room Details</Typography>
                              <Typography variant="body2">Bed {patient.bed_number || '-'}/Room {patient.room_number || '-'}</Typography>
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography variant="caption" color="text.secondary" display="block">Consultant</Typography>
                              <Typography variant="body2" noWrap title={task.doctor_name || '-'}>{task.doctor_name || '-'}</Typography>
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
              {taskList.length === 0 && !isLoader && (
                <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>No upcoming tasks</Typography>
              )}
            </>
          )}
        </Box>

        {popupOpen && selectedTask && (
          <VitalsTaskPopup
            task={selectedTask}
            patient={patientList.find((p) => p.ip_admission_id === selectedTask.ip_admission_id) || {}}
            onClose={closePopup}
            onSuccess={() => { showSuccess('Vitals recorded.'); closePopup(); fetchTaskList(); }}
            showError={showError}
          />
        )}

        {treatmentPopupOpen && treatmentTask && (
          <TreatmentTaskPopup
            task={treatmentTask}
            patient={patientList.find((p) => p.ip_admission_id === treatmentTask.ip_admission_id) || {}}
            onClose={closeTreatmentPopup}
            onSuccess={() => { showSuccess('Treatment plan submitted.'); closeTreatmentPopup(); fetchSpecialTaskList(); }}
            showError={showError}
            history={history}
          />
        )}

        {isErrorMsg && <ToastMsg severity="error" msg={isErrorMsgText} msgPop={msgClose} />}
        {successMsg && <ToastMsg severity="success" msg={successMsgText} msgPop={msgClose} />}
        <Loader loaderOpen={isLoader} />
      </Box>
    </Box>
  );
}

function VitalsTaskPopup({ task, patient, onClose, onSuccess, showError }) {
  const [values, setValues] = useState({});
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const fieldNames = task?.field_names || [];

  useEffect(() => {
    const init = {};
    fieldNames.forEach((f) => { init[f.field_name] = f.value != null && f.value !== '' ? String(f.value) : ''; });
    setValues(init);
    setErrors({});
  }, [task?.id]);

  const setValue = (fieldName, value) => {
    setValues((prev) => ({ ...prev, [fieldName]: value }));
    if (errors[fieldName]) setErrors((prev) => ({ ...prev, [fieldName]: '' }));
  };

  const getUnit = (fieldName) => {
    const u = { weight: 'kg', height: 'cm', bpsystolic: 'mm/Hg', bpdistolic: 'mm/Hg', temp: '°C', pulse: '/min', spo2: '%', blood_glucose: 'mg/dL' };
    return u[fieldName] || '';
  };

  const validate = () => {
    const err = {};
    fieldNames.forEach((f) => {
      const v = values[f.field_name];
      if (v === '' || v === undefined) err[f.field_name] = 'Required';
    });
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const handleClear = () => {
    const init = {};
    fieldNames.forEach((f) => { init[f.field_name] = ''; });
    setValues(init);
    setErrors({});
  };

  const handleAdd = () => {
    if (!fieldNames?.length) { showError('No vital fields configured.'); return; }
    if (!validate()) return;
    if (!task?.id) { showError('No schedule found.'); return; }
    const current_schedule = fieldNames.map((f) => ({
      id: f.id,
      schedule_id: task.id,
      value: values[f.field_name] === '' ? null : Number(values[f.field_name]),
    }));
    const payload = {
      id: task.id,
      field_names: current_schedule,
    }
    setSubmitting(true);
    RestAPIService.create(payload, Serviceurls.IP_VITAL_TASK_UPDATE_LOG)
      .then((res) => {
        setSubmitting(false);
        if (res.data?.status === 'success') onSuccess(res.data?.message);
        else showError(res.data?.message || 'Failed to save.');
      })
      .catch((err) => {
        setSubmitting(false);
        showError(err?.response?.data?.message || err?.message || 'Failed to save.');
      });
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        right: 0,
        width: { xs: '100%', sm: 420 },
        maxWidth: '100%',
        height: '100%',
        bgcolor: 'background.paper',
        boxShadow: '-4px 0 20px rgba(0,0,0,0.1)',
        zIndex: 1300,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        borderLeft: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: 'background.paper' }}>
        <Typography variant="h6" fontWeight={600}>Vitals Task</Typography>
        <IconButton size="small" onClick={onClose}><CloseIcon /></IconButton>
      </Box>
      <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontWeight: 600 }}>PATIENT DETAILS</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
          <Box component="img" src={patient.photo_url || (patient.patient_gender === 'm' || patient.patient_gender === 'Male' ? ImagePaths.Male?.default : ImagePaths.Female?.default)} alt="" sx={{ width: 56, height: 56, borderRadius: '50%', objectFit: 'cover' }} />
          <Box>
            <Typography variant="subtitle1" fontWeight={500}>{patient.patient_name} {patient.patient_age}</Typography>
            <Typography variant="body2" color="text.secondary">{patient.patient_account_number} | {patient.mobile_number}</Typography>
          </Box>
        </Box>

        <Typography variant="body2" color="text.secondary">Scheduled: {task?.scheduled_time || '-'}</Typography>
        <Typography variant="body2" color="text.secondary">End: {task?.end_date} {task?.end_time ? `| ${task.end_time}` : ''}</Typography>
        <Typography variant="body2" sx={{ mt: 1 }}>Instruction: {task?.instructions || '-'}</Typography>

        {fieldNames?.length > 0 && (
          <>
            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>Current</Typography>
            <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', '& td': { py: 1.25, pr: 2 }, '& th': { py: 1.25, textAlign: 'left', fontWeight: 600, borderBottom: '1px solid', borderColor: 'divider' }, '& tbody tr': { borderBottom: '1px solid', borderColor: 'divider' } }}>
              <thead>
                <tr>
                  <th>VITALS</th>
                  <th>VALUES</th>
                </tr>
              </thead>
              <tbody>
                {fieldNames.map((f) => (
                  <tr key={f.field_name}>
                    <td>{f.label || f.field_name}</td>
                    <td>
                      <TextField
                        size="small"
                        type="number"
                        value={values[f.field_name] ?? ''}
                        onChange={(e) => setValue(f.field_name, e.target.value)}
                        error={!!errors[f.field_name]}
                        helperText={errors[f.field_name]}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">{getUnit(f.field_name)}</InputAdornment>,
                        }}
                        sx={{ maxWidth: 140 }}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </Box>
          </>
        )}
      </Box>
      <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider', display: 'flex', gap: 2, justifyContent: 'flex-end', bgcolor: 'background.paper' }}>
        <Button variant="outlined" onClick={handleClear} sx={{ textTransform: 'capitalize' }}>Clear</Button>
        <Button variant="contained" onClick={handleAdd} disabled={submitting} sx={{ textTransform: 'capitalize' }}>Add</Button>
      </Box>
    </Box>
  );
}

function TreatmentTaskPopup({ task, patient, onClose, onSuccess, showError }) {
  const [remarks, setRemarks] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handlePost = () => {
    if (!task?.id) return;
    setSubmitting(true);
    const payload = {
      id: [task.id],
      get_type: 'Care',
      remark: remarks.trim(),
    };
    RestAPIService.create(payload, Serviceurls.IP_PATIENT_NURSE_TASKS)
      .then((res) => {
        setSubmitting(false);
        if (res.data?.status === 'success') onSuccess(res.data?.message);
        else showError(res.data?.message || 'Failed to submit.');
      })
      .catch((err) => {
        setSubmitting(false);
        showError(err?.response?.data?.message || err?.message || 'Failed to submit.');
      });
  };

  const taskDrugs = task?.drugs || [];

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        right: 0,
        width: { xs: '100%', sm: 420 },
        maxWidth: '100%',
        height: '100%',
        bgcolor: 'background.paper',
        boxShadow: '-4px 0 20px rgba(0,0,0,0.1)',
        zIndex: 1300,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        borderLeft: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6" fontWeight={600}>Treatment Task</Typography>
        <IconButton size="small" onClick={onClose}><CloseIcon /></IconButton>
      </Box>
      <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontWeight: 600 }}>PATIENT DETAILS</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
          <Box component="img" src={patient.photo_url || (patient.patient_gender === 'm' || patient.patient_gender === 'Male' ? ImagePaths.Male?.default : ImagePaths.Female?.default)} alt="" sx={{ width: 56, height: 56, borderRadius: '50%', objectFit: 'cover' }} />
          <Box>
            <Typography variant="subtitle1" fontWeight={500}>{patient.patient_name} {patient.patient_age}</Typography>
            <Typography variant="body2" color="text.secondary">{patient.patient_account_number} | {patient.mobile_number}</Typography>
          </Box>
        </Box>

        <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>TASK DETAILS</Typography>
        <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'divider', mb: 2 }}>
          <Typography variant="body2"><strong>Task:</strong> {task?.task_name || '-'}</Typography>
          {task?.duration != null && <Typography variant="body2"><strong>Duration:</strong> {task.duration} {task.duration_mode || 'Minutes'}</Typography>}
          {task?.instructions && <Typography variant="body2"><strong>Instructions:</strong> {task.instructions}</Typography>}
        </Box>

        <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>MEDICINE</Typography>
        {taskDrugs.length > 0 ? (
          <Box sx={{ mb: 2 }}>
            {taskDrugs.map((drug, idx) => (
              <Box key={idx} sx={{ py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Typography variant="body2">{drug.drug_name} – {drug.quantity} {drug.uom}</Typography>
                {(drug.combinations || []).map((c, ci) => (
                  <Typography key={ci} variant="caption" display="block" sx={{ pl: 2 }}>+ {c.drug_name} – {c.quantity} {c.uom}</Typography>
                ))}
              </Box>
            ))}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>No medicine</Typography>
        )}

        <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>REMARKS</Typography>
        <TextField fullWidth multiline rows={3} size="small" placeholder="Remarks" value={remarks} onChange={(e) => setRemarks(e.target.value)} />
      </Box>
      <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider', display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <Button variant="outlined" onClick={onClose} sx={{ textTransform: 'capitalize' }}>Cancel</Button>
        <Button variant="contained" onClick={handlePost} disabled={submitting} sx={{ textTransform: 'uppercase', bgcolor: Colors.Primary }}>Complete</Button>
      </Box>
    </Box>
  );
}
